import { useQuery as useTanstackQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'urql';

import { useIsRevelioAdmin } from '@revelio/auth';

import { useKibanaLogger } from '../../../../shared/hooks/kibana-logger/kibana-logger';
import { AllClientsQuery } from '../../../adminRewrite/userOperations';
import { fetchDataBuilderResults } from '../../api/data-builder-api';
import { useBuildDataBuilderPayload } from '../../api/useBuildDataBuilderPayload';
import { useDataBuilderQuery } from '../../api/useDataBuilderQuery';
import {
  type CompanySelectionGateStatus,
  useCompanySelectionGate,
} from '../../CompanySelectionGateContext';
import {
  selectDeliverableClient,
  selectDeliverableName,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../../data-builder.store';
import { submitDeliverableWithLogging } from './submit-deliverable';
import { useNavigateToDelivery } from './useNavigateToDelivery';

export type VerifyStatus =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'error'; error: unknown };

export type ConfirmationFormValues = {
  client: string;
  name: string;
};

export type ClientOption = { value: string; label: string };

type UseVerifySubmitResult = {
  control: ReturnType<typeof useForm<ConfirmationFormValues>>['control'];
  register: ReturnType<typeof useForm<ConfirmationFormValues>>['register'];
  errors: ReturnType<
    typeof useForm<ConfirmationFormValues>
  >['formState']['errors'];
  clientOptions: ClientOption[];
  isRevelioAdmin: boolean;
  verifyStatus: VerifyStatus;
  handleConfirmationSubmit: () => void;
  submitButtonText: string;
  errorMessage: string;
  canSubmit: boolean;
  companySelectionGateStatus: CompanySelectionGateStatus;
};

export const useVerifySubmit = (): UseVerifySubmitResult => {
  const { status: companySelectionGateStatus } = useCompanySelectionGate();
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const delayedNavigateToDelivery = useNavigateToDelivery();
  const { data } = useDataBuilderQuery();
  const { data: rawSchema } = useTanstackQuery({
    queryKey: ['data-builder'],
    queryFn: fetchDataBuilderResults,
  });
  const { kibanaLogger } = useKibanaLogger();

  const [{ data: clientsData }] = useQuery({
    query: AllClientsQuery,
    pause: !isRevelioAdmin,
  });

  const storedName = useDataBuilderSelector(selectDeliverableName);
  const storedClient = useDataBuilderSelector(selectDeliverableClient);
  const { setDeliverableName, setDeliverableClient } = useDataBuilderActions();

  const clientOptions: ClientOption[] = sortBy(clientsData?.clients, [
    'client_name',
  ])
    .map((client) => {
      const clientName = client?.client_name;
      return {
        label: typeof clientName === 'string' ? clientName : '',
        value: typeof clientName === 'string' ? clientName : '',
      };
    })
    .filter((option) => option.value !== '');

  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmationFormValues>({
    defaultValues: {
      client: storedClient,
      name: storedName,
    },
  });

  const watchedName = watch('name');
  const watchedClient = watch('client');

  useEffect(() => {
    setDeliverableName(watchedName?.trim() ?? '');
  }, [watchedName, setDeliverableName]);

  useEffect(() => {
    setDeliverableClient(watchedClient ?? '');
  }, [watchedClient, setDeliverableClient]);

  const payloadClient = isRevelioAdmin ? watchedClient || null : null;
  const payloadName = watchedName?.trim() ?? '';

  const { build } = useBuildDataBuilderPayload({
    client: payloadClient,
    name: payloadName,
    datasets: data?.datasets,
  });
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>({
    status: 'idle',
  });

  const handleSubmitDeliverable = async () => {
    if (
      verifyStatus.status === 'pending' ||
      verifyStatus.status === 'success'
    ) {
      return;
    }

    if (rawSchema === undefined) {
      return;
    }

    const payloadResult = build(rawSchema);
    if (payloadResult === null) {
      setVerifyStatus({
        status: 'error',
        error: new Error('Failed to build payload'),
      });
      return;
    }

    setVerifyStatus({ status: 'pending' });
    try {
      await submitDeliverableWithLogging(
        payloadResult.payload,
        kibanaLogger,
        'DataBuilder.useVerifySubmit'
      );
      setVerifyStatus({ status: 'success' });
      await delayedNavigateToDelivery();
    } catch (error: unknown) {
      setVerifyStatus({ status: 'error', error });
    }
  };

  const handleConfirmationSubmit = handleSubmit(() => {
    void handleSubmitDeliverable();
  });

  const submitButtonText = (() => {
    if (verifyStatus.status === 'pending') {
      return 'Submitting...';
    }
    if (companySelectionGateStatus === 'pending') {
      return 'Preparing companies...';
    }
    return 'Submit';
  })();

  const errorMessage =
    verifyStatus.status === 'error' && verifyStatus.error instanceof Error
      ? verifyStatus.error.message
      : 'An error occurred.';

  return {
    control,
    register,
    errors,
    clientOptions,
    isRevelioAdmin,
    verifyStatus,
    handleConfirmationSubmit,
    submitButtonText,
    errorMessage,
    canSubmit:
      verifyStatus.status !== 'pending' &&
      verifyStatus.status !== 'success' &&
      rawSchema !== undefined &&
      companySelectionGateStatus === 'idle',
    companySelectionGateStatus,
  };
};
